/* eslint-disable @typescript-eslint/naming-convention */

import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  forwardRef,
  Input,
  OnChanges,
  OnInit,
  Output,
  QueryList,
  SimpleChanges,
  ViewChild,
  ViewChildren,
} from '@angular/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import {
  IActionMapping,
  ITreeOptions,
  ITreeState,
  KEYS,
  TreeComponent,
  TreeModel,
  TreeNode,
} from '@circlon/angular-tree-component';
import {
  catchError,
  debounceTime,
  distinctUntilChanged,
  map,
  shareReplay,
  switchMap,
  tap,
} from 'rxjs/operators';
import { BehaviorSubject, EMPTY, forkJoin, Observable, Subject, throwError, of } from 'rxjs';
import cloneDeep from 'lodash-es/cloneDeep';
import forEach from 'lodash-es/forEach';
import uniqBy from 'lodash/uniqBy';
import { FilterAutoCompleteInputComponent } from '../autocomplete/search-autocomplete-input.component';
import { FilterContentItem } from '@portal/shared/ui/filter/src/lib/content/item';
import {
  EntityType as EntityTypeEnum,
  IEntity,
  IEntityAncestors,
  IEntityLightWeight,
  IOrganisation,
} from '@portal/entity-services/interfaces';
import { AuthenticationService } from '@portal/shared/auth/authentication/src/lib/authentication.service';
import { OrganisationService } from '@portal/entity-services/organisations/src/lib/services/organisation.service';
import { Direction, LanguageHandle } from '@portal/shared/languages';
import { FILTER_CONTENT_ITEM } from '@portal/shared/ui/filter/src/lib/content/token';
import { ISelectInput } from '../box/interfaces/select-input.interface';
import { SearchSelector } from '../search/enums/search-selector.enum';
import { IFilterToApply } from '../search/interfaces/filter-to-apply.interface';
import { ISearchCriteria } from '../search/interfaces/search-criteria.interface';
import { SearchOperator } from '../search/enums/search-operator.enum';
import { FilterSubMenu } from '../enums/filter-sub-menu.enum';
import { ErrorService } from '@portal/shared/ui/form';
import { UserService } from '@portal/shared/user/src';
import { AuthorizationService } from '@portal/shared/auth/authorization/src';
import { MobileViewService } from '../../../../../../../apps/portal/src/app/services/mobile-view.service';
import { SearchParameterList } from '../lists/search-parameters.list';
import { SearchParameters } from '../enums/search-parameters.enum';
import { Directions } from '@portal/shared/core/src/lib/enums/direction.enum';
import { HttpError } from '@portal/shared/error-handler/src';
import { NotifierService } from '@portal/shared/ui/notifier/src';
import get from 'lodash/get';
import { HttpErrorResponse } from '@angular/common/http';
import { FeatureToggle } from '@portal/shared/auth/feature-toggle/src';
import { Features } from 'environments/enums/features.enum';

declare const $localize;

type SelectedNode = IEntity & { children: SelectedNode[] };

@UntilDestroy()
@Component({
  selector: 'portal-hierarchy-single-filter',
  templateUrl: './hierarchy-single-filter.component.html',
  styleUrls: ['./hierarchy-single-filter.component.scss'],
  providers: [
    {
      provide: FILTER_CONTENT_ITEM,
      useExisting: HierarchySingleFilterComponent,
    },
  ],
  standalone: false,
})
export class HierarchySingleFilterComponent
  extends FilterContentItem
  implements OnInit, OnChanges, AfterViewInit
{
  static initialDebounce = 1000;
  static reachedConstant = 19;

  @Input()
  set searchSelector(val: SearchSelector) {
    this._searchSelector = val;
  }

  @Input()
  set filterName(val: string) {
    this.entityText = val;
  }

  @Input()
  isNewDesignFilter = false;

  get searchSelector(): SearchSelector {
    return this._searchSelector;
  }

  set entityText(val: string) {
    this._entityText = val;
  }

  get entityText(): string {
    return this._entityText;
  }

  @Input() entityType = '';
  @Input() entityRemoved$ = new Subject<ISelectInput>();
  @Input() selectedEntity: Map<string, ISearchCriteria> = new Map(); // Pre-selected, from store
  @Input() selectLimit = 0;
  @Input() displayFromFilter: boolean;
  @Input() displaySubOrganisationCheckBox: boolean;
  @Input() closeSubmenus$ = new Subject<boolean>();
  @Input() notFoundMessage = $localize`No result found`;
  @Input() shouldPullEntityFromStorage = true;

  @Output() filterApplied: EventEmitter<IFilterToApply> = new EventEmitter();
  @Output() filterUpdated: EventEmitter<IFilterToApply> = new EventEmitter();
  @Output() staticFilterUpdated: EventEmitter<IFilterToApply> = new EventEmitter();
  @Output() selectedOrganisation: EventEmitter<IEntity> = new EventEmitter();
  @Output() organisationTextName: EventEmitter<any> = new EventEmitter();
  @Output() listAllEntities: EventEmitter<ISelectInput[]> = new EventEmitter();
  @Output() updateCheckBox: EventEmitter<any> = new EventEmitter();
  @Output() buttonFocused: EventEmitter<boolean> = new EventEmitter();

  @ViewChild('tree') tree: TreeComponent;
  @ViewChild('tree', { read: ElementRef }) treeElementRef: ElementRef;
  @ViewChildren('treeNode') treeNodes: QueryList<ElementRef>;
  @ViewChild(forwardRef(() => FilterAutoCompleteInputComponent))
  searchInput: FilterAutoCompleteInputComponent;

  itemToggled: EventEmitter<number> = new EventEmitter();

  staticListUpdateSubject$ = new Subject<IFilterToApply>();
  loading$: Observable<boolean>;
  loadingMore$ = new BehaviorSubject(0);
  loadingNewPortion$: Observable<boolean>;
  hierarchyViewLoading$: Observable<boolean>;
  searchTerm$ = new Subject<string>();
  search$: Observable<IOrganisation[]>;
  nextPage$ = new Subject<number>();
  pagination$: Observable<IOrganisation[]>;
  showSelectedEntity: Map<string, ISelectInput> = new Map();
  localPreSelectedEntity: Map<string, ISelectInput> = new Map();
  groupEntityRelationships: IEntityLightWeight[] = [];
  entitiesAll: IEntity[] = [];
  entities: IEntity[] = [];
  allEntities: ISelectInput[];
  selectedNode: SelectedNode;
  searchedText = '';
  title = '';
  isOpen = false;
  hasFilteredText = false;
  isFirstLoad = true;
  debounce = 1000;
  treeState: ITreeState;
  hierarchy = SearchSelector.Hierarchy;
  isCheckedDefault = true;
  isEntityTypeGroup = false;
  treeActionsMapping: IActionMapping = {
    keys: {
      [KEYS.ENTER]: () => {
        this.onFilterApplied();
      },
    },
  };
  options: ITreeOptions = {
    getChildren: (node: TreeNode) => {
      return this.isEntityTypeGroup && node?.data?.entityType === EntityTypeEnum.GROUP
        ? this.callGetGroupRelationships(node)
        : this.callGetHierarchyView(node);
    },
    actionMapping: this.treeActionsMapping,
    useCheckbox: false,
    idField: 'entityUid',
    displayField: 'name',
    rtl:
      this.languageHandle.getCurrentObjectLanguage(this.languageHandle.getBuildLanguage())
        .direction === Direction.RTL,
  };
  isDeviceMobile$: Observable<boolean>;
  searchParameterList = this.searchParameters.list;
  searchParameterType = SearchParameters.Name;
  isFriendlyNameFeatureActive = false;
  isScrollEventAdded = false;
  isLastBatch = false;
  limitSelectedEntityElements = 20;

  get searchPlaceholder(): string {
    return $localize`Search by ${
      this.searchParameterList.find((input) => input.id === this.searchParameterType).text
    }`;
  }
  currentRootEntity = this.authService.entityUid;

  readonly Directions = Directions;
  private selectedPath: string[];
  private start: number;
  private stopLoading: boolean;
  private loadingStatus = false;
  private _searchSelector: any = SearchSelector.EntityUid;
  private _entityText = $localize`Organisations`;
  private readonly entityUidSelector = 'entityUid';
  private readonly entityNameSelector = 'entityName';
  private currentSelectedEntity: IEntity;
  private lastFocusedNodeId: string | number | null = null;
  private isHandlingRightArrow = false;
  private rightArrowTimeoutId: any = null;
  private intendedFocusNodeId: string | null = null;

  constructor(
    private organisationService: OrganisationService,
    private authService: AuthenticationService,
    private languageHandle: LanguageHandle,
    private userService: UserService,
    private authorizationService: AuthorizationService,
    private mobileViewService: MobileViewService,
    private searchParameters: SearchParameterList,
    private notifierService: NotifierService,
    private featureToggleService: FeatureToggle,
  ) {
    super();
    this.loading$ = organisationService.loading$.pipe(shareReplay(1));
    this.hierarchyViewLoading$ = organisationService.hierarchyViewLoading$.pipe(shareReplay(1));
    this.loadingNewPortion$ = organisationService.loadingNewPortion$.pipe(
      tap((value) => (this.loadingStatus = value)),
    );
    this.isDeviceMobile$ = this.mobileViewService.mobileDevice;
    this.start = 0;
    this.isEntityTypeGroup =
      this.authorizationService['user'].entity.entityType === EntityTypeEnum.GROUP;
  }

  ngOnInit(): void {
    if (this.shouldPullEntityFromStorage) {
      this.getEntityFromStorage();
    }
    this.isFriendlyNameFeatureActive = this.featureToggleService.isConfigFeatureActive(
      Features.friendlyName,
    );
    this.staticListUpdateSubject$
      .pipe(debounceTime(100))
      .pipe(untilDestroyed(this))
      .subscribe(() => {
        this.staticFilterUpdated.emit({
          key: this.searchSelector,
          value: Array.from(this.localPreSelectedEntity.values()),
        });
      });

    this.updateText();
    this.startListeningForSearch();
    this.startListeningForPageChange();
    this.itemToggled.pipe(untilDestroyed(this)).subscribe(() => (this.start = 0));

    this.entityRemoved$.pipe(untilDestroyed(this)).subscribe((entityToRemove: ISelectInput) => {
      let selectedEntity;
      if (!this.localPreSelectedEntity) {
        this.removeAllEntities();
        this.start = 0;
        selectedEntity = this.getEntityById(entityToRemove.id);
      } else {
        selectedEntity = this.localPreSelectedEntity.get(entityToRemove.id);
      }
      if (selectedEntity) {
        this.onEntityRemoved(entityToRemove);
        this.onFilterApplied();
        this.itemToggled.emit();
        this.isOpen = false;
      }
    });

    this.closeSubmenus$.subscribe((close: boolean) => {
      if (close && this.isOpen) {
        this.itemToggled.emit();
      }
    });
  }

  ngAfterViewInit(): void {
    this.updateViewportTabIndex();
  }

  updateViewportTabIndex(): void {
    if (this.treeElementRef) {
      const viewport = this.treeElementRef.nativeElement.querySelector('tree-viewport');
      if (viewport) {
        viewport.setAttribute('tabindex', '-1');
      }
    }
  }

  isNodeDisabled(nodeData: any): boolean {
    return nodeData.data?.entityType === EntityTypeEnum.GROUP && this.isEntityTypeGroup === true;
  }

  getEntityFromStorage(): void {
    const { name, entityUid } = this.getStoredEntityDetails();

    if (name && entityUid) {
      this.currentSelectedEntity = { entityUid, name };
      this.handleEntitySelection({ name, entityUid });
    }
  }
  isEntityTypeVisible(node: any, isDeviceMobile: boolean | null): boolean {
    return this.organisationService.isEntityTypeVisible(
      node,
      this.isFriendlyNameFeatureActive,
      isDeviceMobile,
    );
  }

  ngOnChanges(changes: SimpleChanges): void {
    const selectedEntity = changes.selectedEntity?.currentValue?.get(SearchSelector.EntityUid);
    if (selectedEntity) {
      this.setSelectedEntities(selectedEntity);
    }

    this.updateText();
    this.updateSelectedEntities();
  }

  onScrolled(event: {
    target: { offsetHeight: number; scrollTop: number; scrollHeight: number };
  }): void {
    if (
      event.target.offsetHeight + event.target.scrollTop >= event.target.scrollHeight &&
      !this.stopLoading &&
      !this.loadingStatus
    ) {
      this.nextPage$.next(this.start);
    }
  }

  openContent(): void {
    this.itemToggled.emit();
    this.isOpen = true;
  }

  onLoadMore(node: TreeNode): void {
    if (node.data.children.length >= node.data.count) {
      this.isLastBatch = true;
      return;
    }
    if (node.data.count - node.data.children.length <= this.limitSelectedEntityElements) {
      this.isLastBatch = true;
    }
    this.loadingMore$.next(node.id);
    this.organisationService
      .getHierarchyView(
        node.data.entityUid,
        node.data.children.length === 1 ? 0 : node.data.children.length,
      )
      .subscribe(
        (value) => {
          node.data.children.push(
            ...(this.selectedNode?.entityUid
              ? value.filter((value1) => !this.selectedPath.includes(value1.entityUid))
              : value),
          );
          this.loadingMore$.next(0);
          this.tree.treeModel.update();
        },
        () => {},
        () => {
          this.loadingMore$.next(0);
        },
      );
  }

  onActivateEntity(selectedEntity: IEntity): void {
    this.currentSelectedEntity = selectedEntity;
  }

  onDeactivateEntity(deactivatedEntity?: IEntity): void {
    if (deactivatedEntity?.entityUid === this.currentSelectedEntity?.entityUid) {
      return;
    }
    this.currentSelectedEntity = null;
  }

  clearSelectedEntity(): void {
    this.clear();
  }

  onActivateItem(selectedEntity: IEntity): void {
    if (!selectedEntity) {
      return;
    }
    this.localPreSelectedEntity.clear();
    this.storeEntityInAppropriateStorage(selectedEntity);

    this.localPreSelectedEntity.set(selectedEntity?.entityUid, {
      id: selectedEntity?.entityUid,
      text: selectedEntity?.name,
    });
  }

  storeEntityInAppropriateStorage(selectedEntity: IEntity): void {
    const storage = window.location.pathname.includes('/reports/report-scheduler')
      ? sessionStorage
      : localStorage;
    storage.setItem(this.entityUidSelector, selectedEntity.entityUid);
    storage.setItem(this.entityNameSelector, selectedEntity.name);
  }

  onDeactivateItem(): void {
    this.currentSelectedEntity = null;
    if (this.localPreSelectedEntity.size) {
      localStorage.removeItem(this.entityUidSelector);
      localStorage.removeItem(this.entityNameSelector);
      this.localPreSelectedEntity.clear();
    }
  }

  onActivateOrDeactivateItem(selectedEntity?: IEntity): void {
    if (selectedEntity) {
      this.onActivateItem(selectedEntity);
    } else {
      this.onDeactivateItem();
    }
  }

  onSearch(searchValue = ''): void {
    this.searchedText = searchValue;
    this.start = 0;
    this.searchTerm$.next(searchValue);
  }

  onFilterApplied(isCalledFromHtml?: boolean): void {
    if (isCalledFromHtml) {
      this.onActivateItem(this.currentSelectedEntity);
    }
    if (!this.currentSelectedEntity) {
      this.onDeactivateItem();
    }
    this.updateEntities();
    this.start = 0;

    const values: string[] = this.getValues();
    if (values.length) {
      this.filterApplied.emit({
        selector: this.searchSelector,
        key: this.searchSelector,
        operator: SearchOperator.In,
        value: Array.from(this.localPreSelectedEntity.values()),
        argument: `${Array.from(values).join(',')}`,
      });
      this.staticFilterUpdated.emit({
        key: this.searchSelector,
        selector: this.searchSelector,
        value: Array.from(this.localPreSelectedEntity.values()),
      });
    } else {
      this.filterApplied.emit({
        key: this.searchSelector,
        selector: this.searchSelector,
        toRemove: true,
      });
      this.staticFilterUpdated.emit({
        key: this.searchSelector,
        selector: this.searchSelector,
        toRemove: true,
      });
    }

    this.updateText();
    this.itemToggled.emit();
    this.isOpen = false;
  }

  onKeyDownSpace(): void {
    this.isOpen = !this.isOpen;
  }

  getList(): Observable<IOrganisation[]> {
    this.debounce = 0;
    this.searchedText = '';
    this.searchInput.reset();
    this.searchTerm$.next(this.searchedText);
    return null;
  }

  groupEntityWillBeNotPreSelected(): void {
    if (
      this.localPreSelectedEntity.get(this.authorizationService['user']?.entity?.entityUid) &&
      this.authorizationService['user'].entity.entityType === EntityTypeEnum.GROUP
    ) {
      this.localPreSelectedEntity.delete(this.authorizationService['user']?.entity?.entityUid);
    }
  }

  getValues(): string[] {
    const values = new Set<string>();
    this.groupEntityWillBeNotPreSelected();
    Array.from(this.localPreSelectedEntity.values()).forEach((val: ISelectInput) => {
      values.add(val.id);
    });
    return Array.from(values);
  }

  clear(): void {
    this.removeAllEntities();
    this.resetInputValue();
  }

  resetInputValue(): void {
    this.onSearch('');
    this.searchParameterType = SearchParameters.Name;
  }

  removeAllEntities(): void {
    this.localPreSelectedEntity.clear();
    this.currentSelectedEntity = null;
    this.updateSelectedEntities();
    this.updateText();
  }

  onCheckboxToggle(): void {
    this.updateCheckBox.emit();
  }

  onStateChange(state: ITreeState): void {
    this.treeState = state;

    // Keep restoring focus during right arrow key handling
    if (this.isHandlingRightArrow && this.intendedFocusNodeId) {
      const node = this.tree.treeModel.getNodeById(this.intendedFocusNodeId);
      if (node) {
        this.tree.treeModel.setFocusedNode(node);
        return; // BLOCK tree focus override
      }
    }

    if (state.focusedNodeId && state.focusedNodeId !== this.lastFocusedNodeId) {
      this.lastFocusedNodeId = state.focusedNodeId;
      this.scrollToFocusedNode();
    }
  }

  onFocusIn(event: FocusEvent, treeModel: TreeModel): void {
    const treeElement = event.currentTarget as HTMLElement;

    if (event.target === treeElement) {
      treeModel.setFocus(true);
      if (!treeModel.focusedNode) {
        const firstNode = treeModel.getFirstRoot();
        if (firstNode) {
          treeModel.setFocusedNode(firstNode);
        }
      }
    }
  }

  onFocusOut(event: FocusEvent, treeModel: TreeModel): void {
    if (event.target === event.currentTarget) {
      treeModel.setFocus(false);
    }
  }

  onKeyDownArrowRight(event: KeyboardEvent, treeModel: TreeModel): void {
    event.preventDefault();
    event.stopPropagation();

    const node = treeModel.focusedNode || treeModel.getFirstRoot();
    if (!node) return;

    // Enable focus override control
    this.isHandlingRightArrow = true;

    // Expand and keep focus on the same node
    if (!node.isExpanded && node.hasChildren) {
      this.intendedFocusNodeId = node.id;

      node.expand();
      treeModel.setFocusedNode(node);

      this.resetRightArrowFlag(600);
      return;
    }

    // Move focus to first child if already expanded
    if (node.isExpanded && node.hasChildren) {
      const firstChild = node.getFirstChild();
      if (firstChild) {
        this.intendedFocusNodeId = firstChild.id;
        treeModel.setFocusedNode(firstChild);
      }
    }

    this.resetRightArrowFlag(250);
  }

  onFilterClear(): void {
    this.removeAllEntities();
    this.onFilterApplied();
    this.searchInput.reset();
    this.searchTerm$.next('');
    this.selectedNode = null;
    this.treeState = null;
    this.itemToggled.emit();
    this.filterUpdated.emit();
  }

  private resetRightArrowFlag(delay = 250): void {
    if (this.rightArrowTimeoutId) {
      clearTimeout(this.rightArrowTimeoutId);
    }
    this.rightArrowTimeoutId = setTimeout(() => {
      this.isHandlingRightArrow = false; // Disable focus override control
    }, delay);
  }

  private onEntityRemoved(selectedEntity: ISelectInput): void {
    this.localPreSelectedEntity.delete(selectedEntity.id);
    this.currentSelectedEntity = null;
  }

  private startListeningForPageChange(): void {
    this.pagination$ = this.paginate(this.nextPage$);
    this.pagination$.pipe(untilDestroyed(this)).subscribe((data) => {
      this.entities = [...this.entities, ...data];
      this.entitiesAll = [...this.entitiesAll, ...data];
      if (this.localPreSelectedEntity.size) {
        this.updateEntities();
      }
    });
  }

  private paginate(terms: Observable<number>): Observable<IOrganisation[]> {
    return terms.pipe(debounceTime(500)).pipe(
      switchMap((page: number) => {
        this.start = page + 20;
        return this.getEntitiesByName(this.searchedText, true);
      }),
    );
  }

  private getEntitiesByName(term: string, noLoader?: boolean): Observable<IOrganisation[]> {
    const reachedTheEnd = (organisation: IOrganisation[]): boolean => {
      this.hasFilteredText = Boolean(term);
      this.stopLoading = organisation.length < HierarchySingleFilterComponent.reachedConstant;
      return this.stopLoading;
    };

    const limit = undefined;
    const isLightweight = true;
    const params = null;
    const isUidSearch = this.searchParameterType === SearchParameters.EntityUid;

    if (term) {
      if (isUidSearch) {
        const useBasicUidLookup =
          this.entityType !== ''
            ? this.organisationService.getByKey(term).pipe(map((value) => [value]))
            : this.organisationService.getByKeyWithAncestors(term, false, null).pipe(
                map((value) => (value ? [value] : [])),
                catchError((err) => {
                  if (err.error.status === HttpError.CodeError404) return of([]);
                  return throwError(err);
                }),
              );
        return useBasicUidLookup;
      }

      return this.organisationService
        .getLightWeightWithQuery(
          { [this.searchParameterType]: term },
          noLoader,
          this.start,
          limit,
          isLightweight,
          this.isEntityTypeGroup,
        )
        .pipe(tap(reachedTheEnd));
    }

    return this.organisationService
      .getEntity(this.start, noLoader, this.currentRootEntity, params, this.isEntityTypeGroup)
      .pipe(tap(reachedTheEnd));
  }

  private setSelectedEntities(selectedEntity: ISearchCriteria): void {
    // Set selectedFilters
    const values = new Set<string>(
      selectedEntity.argument.replace('(', '').replace(')', '').split(','),
    );
    const selectedForState = {};
    this.localPreSelectedEntity.clear();
    forEach(Array.from(values), (argument: string) => {
      selectedForState[argument] = true;
      this.localPreSelectedEntity.set(argument, {
        id: argument,
        text: '',
      });
    });

    this.treeState = this.treeState
      ? {
          ...this.treeState,
          activeNodeIds: selectedForState,
        }
      : { activeNodeIds: selectedForState };

    this.updateSelectedEntities();
  }

  private startListeningForSearch(): void {
    this.search$ = this.search(this.searchTerm$);
    this.search$.pipe(untilDestroyed(this)).subscribe((entities: IEntity[]) => {
      if (this.selectedNode && entities[0]?.hasChildren) {
        this.expandTree();
      }
      this.entities = entities;
      this.entitiesAll = entities;
      if (this.localPreSelectedEntity.size) {
        this.updateEntities();
        this.selectedPath = undefined;
        this.selectedNode = undefined;
        Array.from(this.localPreSelectedEntity.values()).forEach((value) => {
          this.treeState.selectedLeafNodeIds[value.id] = true;
          const id = value.id;
          if (id !== this.currentRootEntity && !this.selectedPath?.includes(id)) {
            this.organisationService
              .getAncestors(id)
              .pipe(
                catchError((err) => {
                  const code = ErrorService.getStatusCode(err);
                  if (code === HttpError.CodeError404) return EMPTY;
                  return throwError(err);
                }),
              )
              .subscribe(
                (ancestors) => {
                  this.flatten(ancestors);
                },
                (error: HttpErrorResponse) => {
                  this.notifierService.error(
                    $localize`Error` +
                      ` (${get(error, 'error.status')}) ${get(error, 'error.error.message')}`,
                  );
                },
              );
          }
        });
      }
    });
  }

  private updateSelectedEntities(): void {
    this.showSelectedEntity = cloneDeep(this.localPreSelectedEntity);
    let entitiesToFetch: string;
    Array.from(this.showSelectedEntity.values()).forEach((val: ISelectInput) => {
      if (!val.text) {
        entitiesToFetch = val.id;
      }
    });

    if (entitiesToFetch) {
      this.getEntityById(entitiesToFetch)
        .pipe(
          untilDestroyed(this),
          catchError((err) => {
            const httpStatusCode = ErrorService.getStatusCode(err);
            switch (httpStatusCode) {
              case 403:
              case 404:
                this.onDeactivateItem();
                this.updateSelectedEntities();

                this.onFilterApplied();
                this.itemToggled.emit();
                this.isOpen = false;

                this.filterUpdated.emit();
                this.updateText();
                return EMPTY;
              default:
                return throwError(err);
            }
          }),
        )
        .subscribe((org: IOrganisation) => {
          this.localPreSelectedEntity.set(org.entityUid, {
            id: org.entityUid,
            text: org.name,
          });

          if (!this.currentSelectedEntity) {
            this.currentSelectedEntity = {
              name: org.name,
              entityUid: org.entityUid,
            };
          }

          // Just we update if not we lose the results
          // in the filters after to come back from details
          this.onFilterApplied();
          this.updateText();
        });
    } else {
      this.treeState = {
        ...this.treeState,
        activeNodeIds: null,
      };
    }
  }

  private search(terms: Observable<string>): Observable<IOrganisation[]> {
    return terms.pipe(distinctUntilChanged(), debounceTime(this.debounce)).pipe(
      switchMap((term: string) => {
        // Always reset debounce time
        if (this.treeState) {
          this.treeState.expandedNodeIds = {};
          this.treeState.selectedLeafNodeIds = {};
          this.tree?.treeModel?.update();
        }
        this.debounce = HierarchySingleFilterComponent.initialDebounce;
        return this.getEntitiesByName(term);
      }),
      tap(() => this.tree?.sizeChanged()),
    );
  }

  private getEntityById(id: string): Observable<IOrganisation> {
    return this.organisationService.getByKey(id, true);
  }

  private updateEntities(): void {
    if (this.selectLimit > 0) {
      this.localPreSelectedEntity = new Map(
        [...this.localPreSelectedEntity.entries()].slice(0, this.selectLimit),
      );
    }

    this.showSelectedEntity = cloneDeep(this.localPreSelectedEntity);
    const id = Array.from(this.showSelectedEntity.values())[0]?.id;
    if (id && id !== this.authService.entityUid && !this.isEntityTypeGroup) {
      this.organisationService.getAncestors(id).subscribe((value) => {
        this.flatten(value);
      });
    } else {
      this.treeExpand();
    }
    const activeNodeIds = this.treeState?.activeNodeIds;
    if (activeNodeIds && id) {
      activeNodeIds[id] = true;
    }
    this.tree?.treeModel?.setState(this.treeState);
    this.entities = this.entitiesAll;

    this.allEntities = this.entitiesAll?.map((entity) => {
      return {
        id: entity.entityUid,
        text: entity.name,
      };
    });

    this.listAllEntities.emit(this.allEntities);
    this.organisationTextName.emit(FilterSubMenu.Organisation);
  }

  private updateText(): void {
    const values: string[] = this.getValues();
    // important not to change this line
    const text = this.entityText;
    if (values.length) {
      this.title = `${text} (+${values.length})`;
    } else {
      this.title = text;
    }
  }

  private async flatten(data: IEntityAncestors): Promise<any> {
    const result = [];
    while (data) {
      result.push(data);
      if (data?.parent?.entityUid === this.authService.entityUid) {
        break;
      }
      data = data.parent;
    }
    result.map((value) => {
      delete value.parent;
      return value;
    });
    this.selectedPath = result.map((value) => value.entityUid);
    const response = await forkJoin(
      result.map((val) =>
        this.organisationService.getTotalCount({ parentId: val[SearchSelector.EntityUid] }),
      ),
    )
      .pipe(
        map((val) =>
          val.map((totalCountData, index) => ({
            count: totalCountData.count,
            ...result[index],
            hasChildren: totalCountData.count > 0,
            isExpanded: index !== 0,
          })),
        ),
      )
      .toPromise();
    const length = result.length;
    const selectedNode = response[length - 1];
    this.insertChild(selectedNode, response);
    this.selectedNode = selectedNode;

    this.expandTree();
  }

  private insertChild(
    selectedNode: SelectedNode,
    array: SelectedNode[],
    index: number = array.length - 2,
  ): void {
    if (index >= 0 && selectedNode) {
      selectedNode.children = [array[index]];
      this.insertChild(selectedNode.children[0], array, index - 1);
    }
  }

  private expandTree(): void {
    if (!Array.isArray(this.selectedPath) || this.selectedPath.length === 0) {
      return;
    }

    const expandedNodeIds = this.entities[0]?.hasChildren
      ? { [this.entities[0].entityUid]: true }
      : {};
    this.selectedPath.forEach((value, index) => {
      if (!index) {
        return;
      }
      expandedNodeIds[value] = true;
      const someNode = this.tree?.treeModel?.getNodeById(value);
      someNode?.expand();
    });

    this.treeState = {
      ...this.treeState,
      expandedNodeIds,
    };
  }

  private getStoredEntityDetails(): { name: string | null; entityUid: string | null } {
    const isReportSchedulerPage = window.location.pathname.includes('/reports/report-scheduler');
    const storage = isReportSchedulerPage ? sessionStorage : localStorage;

    return {
      name: storage.getItem(this.entityNameSelector) || this.userService.userDetails?.entity?.name,
      entityUid:
        storage.getItem(this.entityUidSelector) || this.userService.userDetails?.entity?.entityUid,
    };
  }

  private handleEntitySelection(entity: { name: string; entityUid: string }): void {
    this.onActivateItem(entity);
    this.setSelectedEntities({ argument: entity.entityUid });
    this.onFilterApplied();
    this.itemToggled.emit();
    this.isOpen = false;
  }

  private treeExpand(): void {
    if (this.isEntityTypeGroup) {
      setTimeout(() => {
        if (
          this.localPreSelectedEntity?.size &&
          this.authService.entityUid !== Array.from(this.localPreSelectedEntity?.values())[0].id
        )
          this.tree?.treeModel?.expandAll();
      }, 0);
    }
  }

  private callGetHierarchyView(node: TreeNode): Promise<any> {
    const onScroll = (event: any): void => {
      const element = event.target;
      const atBottom = Math.floor(element.scrollHeight - element.scrollTop) <= element.clientHeight;
      if (atBottom && !this.loadingMore$.value && !this.isLastBatch) {
        const parentNode = this.tree?.treeModel?.getNodeById(this.currentRootEntity);
        this.onLoadMore(parentNode);
      }
    };

    if (document.querySelector('tree-viewport') && !this.isScrollEventAdded) {
      document.querySelector('tree-viewport').addEventListener('scroll', onScroll);
      this.isScrollEventAdded = true;
    }
    return this.organisationService
      .getHierarchyView(node.id)
      .pipe(
        map((hierarchyList) =>
          hierarchyList.filter((item) => item.entityUid !== this.selectedNode?.entityUid),
        ),
        map((value) =>
          node.id === this.authService.entityUid
            ? [...(this.selectedNode ? [this.selectedNode] : []), ...value]
            : value,
        ),
      )
      .toPromise();
  }

  private callGetGroupRelationships(node: TreeNode): Promise<any> {
    return this.organisationService
      .getGroupRelationships(node.id)
      .pipe(
        map((hierarchyList) =>
          hierarchyList.filter((item) => item.entityUid !== this.selectedNode?.entityUid),
        ),
        map((entities) => {
          this.groupEntityRelationships = cloneDeep(uniqBy(entities, SearchSelector.EntityUid));
          this.groupEntityRelationships?.forEach((entity) => {
            if (entity?.entityUid) {
              const childNode = {
                id: entity?.entityUid,
                data: entity,
              };
              this.setDescendants(childNode as TreeNode);
            }
          });
          return node.id === this.authService.entityUid
            ? [...(this.selectedNode ? [this.selectedNode] : []), ...entities]
            : entities;
        }),
      )
      .toPromise();
  }

  private setDescendants(node: TreeNode): void {
    this.organisationService.getTotalCount({ parentId: node.id }).subscribe((entityCount) => {
      if (entityCount?.count) {
        const entitiesTree = this.entities;
        entitiesTree[0]?.children?.forEach((entity) => {
          if (node?.id === entity.entityUid) {
            entity.count = entityCount?.count;
            entity.hasChildren = true;
          }

          if (!this.treeState?.selectedLeafNodeIds) {
            this.treeState = {
              ...this.treeState,
              selectedLeafNodeIds: {
                ...this.treeState?.selectedLeafNodeIds,
                [entity.entityUid]: false,
              },
            };
          } else {
            this.treeState.selectedLeafNodeIds = {
              ...this.treeState.selectedLeafNodeIds,
              [entity.entityUid]: false,
            };
          }
          this.tree?.treeModel?.setState(this.treeState);
          this.tree?.treeModel?.update();
          this.tree?.sizeChanged();
        });
        this.entities = entitiesTree;
      }
    });
  }

  private scrollToFocusedNode(): void {
    const focusedNodeId = this.treeState?.focusedNodeId;
    if (focusedNodeId && this.treeNodes) {
      setTimeout(() => {
        const focusedNode = this.treeNodes.find(
          (node) => String(node.nativeElement.getAttribute('data-id')) === String(focusedNodeId),
        );
        focusedNode?.nativeElement.scrollIntoView({ block: 'nearest', inline: 'nearest' });
      });
    }
  }
}
